import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Plus, Filter } from "lucide-react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { StatusBadge } from "@/components/StatusBadge";
import { Avatar } from "@/components/Avatar";
import { STATUS_META, STATUS_ORDER } from "@/lib/status";

export default function Customers() {
  const navigate = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    const params = {};
    if (search) params.search = search;
    if (status) params.status = status;
    try {
      const r = await api.get("/customers", { params });
      setCustomers(r.data);
    } finally {
      setLoading(false);
    }
  }, [search, status]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  return (
    <div className="p-8 max-w-[1400px] animate-in-up">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-display text-3xl font-bold tracking-tight">Customers</h1>
          <p className="text-muted-foreground mt-1">{customers.length} lead{customers.length !== 1 ? "s" : ""}</p>
        </div>
        <Button data-testid="customers-new-lead-button" onClick={() => navigate("/new")} className="gap-2 font-semibold">
          <Plus className="h-4 w-4" /> New Lead
        </Button>
      </div>

      <div className="flex flex-col sm:flex-row gap-3 mb-5">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            data-testid="customer-search-input"
            placeholder="Search name, company, phone, passport, country…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>
        <div className="flex items-center gap-1.5 flex-wrap">
          <button
            data-testid="filter-all"
            onClick={() => setStatus("")}
            className={`text-xs font-medium px-3 py-1.5 rounded-full border transition-colors ${
              status === "" ? "bg-primary text-white border-primary" : "border-border hover:bg-secondary"
            }`}
          >
            All
          </button>
          {STATUS_ORDER.map((s) => (
            <button
              key={s}
              data-testid={`filter-${s}`}
              onClick={() => setStatus(s === status ? "" : s)}
              className="text-xs font-semibold px-3 py-1.5 rounded-full border transition-colors"
              style={
                status === s
                  ? { background: STATUS_META[s].color, color: "#fff", borderColor: STATUS_META[s].color }
                  : { borderColor: "hsl(240 6% 90%)" }
              }
            >
              {STATUS_META[s].label}
            </button>
          ))}
        </div>
      </div>

      <div className="bg-white border border-border rounded-lg overflow-hidden">
        {loading ? (
          <div className="p-10 text-center text-muted-foreground text-sm">Loading…</div>
        ) : customers.length === 0 ? (
          <div className="p-16 text-center">
            <Filter className="h-8 w-8 mx-auto text-muted-foreground/50 mb-3" />
            <p className="font-medium">No customers found</p>
            <p className="text-sm text-muted-foreground mt-1">Try a different search or create a new lead.</p>
          </div>
        ) : (
          <div className="divide-y divide-border">
            {customers.map((c) => (
              <button
                key={c.id}
                data-testid={`customer-item-${c.id}`}
                onClick={() => navigate(`/customers/${c.id}`)}
                className="w-full flex items-center gap-4 px-5 py-3.5 hover:bg-secondary/60 transition-colors text-left group"
              >
                <Avatar name={c.name || "Unknown"} size={40} />
                <div className="min-w-0 flex-1">
                  <div className="font-semibold text-sm truncate">{c.name || "Unnamed lead"}</div>
                  <div className="text-xs text-muted-foreground truncate">
                    {[c.company, c.phone, c.country].filter(Boolean).join(" · ") || "No details"}
                  </div>
                </div>
                <div className="hidden md:block text-xs text-muted-foreground w-28 truncate">{c.consultant_name}</div>
                <div className="hidden md:block text-xs text-muted-foreground w-24">
                  {c.next_followup_date ? `↻ ${c.next_followup_date}` : "—"}
                </div>
                {c.contact_type && c.contact_type !== "Customer" ? (
                  <span className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-semibold bg-secondary text-foreground">{c.contact_type}</span>
                ) : (
                  <StatusBadge status={c.status} />
                )}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
